/**
 * CategorySidebar - Navigation verticale par catégories (sidebar gauche)
 * Arborescence dépliable : Rayons > Catégories > Sous-catégories
 * Style « marché artisanal » : pastille terracotta, item actif crème #f0e9d8
 */

import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type { Category } from '../../../types/ecommerce.types';

interface CategorySidebarProps {
  categories: Category[];
  selectedCategoryId: string | null;
  selectedSubCategoryId: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  onSelectSubCategory: (subCategoryId: string | null) => void;
  productCounts: Record<string, number>;
  /** Filtre « seulement ce qui vient de nos cultures ». */
  ownProductionOnly: boolean;
  onToggleOwnProduction: (value: boolean) => void;
  ownProductionCount: number;
  promoOnly: boolean;
  onTogglePromo: (checked: boolean) => void;
  promoCount: number;
  noveltyOnly: boolean;
  onToggleNovelty: (v: boolean) => void;
  noveltyCount: number;
  /** Favoris : filtre montré SEULEMENT à un client connecté. */
  favorisOnly?: boolean;
  onToggleFavoris?: (v: boolean) => void;
  favorisCount?: number;
}

export default function CategorySidebar({
  categories,
  selectedCategoryId,
  selectedSubCategoryId,
  onSelectCategory,
  onSelectSubCategory,
  productCounts,
  ownProductionOnly,
  onToggleOwnProduction,
  ownProductionCount,
  promoOnly,
  onTogglePromo,
  promoCount,
  noveltyOnly,
  onToggleNovelty,
  noveltyCount,
  favorisOnly,
  onToggleFavoris,
  favorisCount,
}: CategorySidebarProps) {
  // Rayons ouverts (dépliés)
  const [expandedRayons, setExpandedRayons] = useState<Set<string>>(
    () => new Set(selectedCategoryId ? [selectedCategoryId] : [])
  );

  // Catégories de niveau 1 ouvertes (pour les sous-catégories de niveau 2)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set(selectedSubCategoryId ? [selectedSubCategoryId] : [])
  );

  // Un rayon sans aucun produit visible n'a rien à faire dans la navigation : il
  // n'ouvre que sur une page vide. Concerne notamment le rayon Consignes, dont les
  // articles sont exclus du catalogue client (cf. ecommerce.api.ts). productCounts
  // est déjà récursif (rayon + descendants), cf. EcommercePage.
  const mainCategories = categories.filter(
    (cat) => !cat.parent_id && (productCounts[cat.id] || 0) > 0,
  );
  const totalProducts = Object.values(productCounts).reduce((sum, count) => sum + count, 0);

  const getChildren = (parentId: string) =>
    categories.filter((cat) => cat.parent_id === parentId);

  const toggleRayon = (rayonId: string) => {
    setExpandedRayons((prev) => {
      const next = new Set(prev);
      if (next.has(rayonId)) {
        next.delete(rayonId);
      } else {
        next.add(rayonId);
      }
      return next;
    });
  };

  const toggleCategory = (catId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  };

  const handleSelectRayon = (rayonId: string) => {
    onSelectCategory(rayonId);
    onSelectSubCategory(null);
    // Auto-expand quand on sélectionne
    setExpandedRayons((prev) => new Set(prev).add(rayonId));
  };

  const handleSelectSubCategory = (subCatId: string, parentRayonId: string) => {
    // Déterminer si c'est un niveau 1 (enfant direct du rayon) ou niveau 2
    const subCat = categories.find((c) => c.id === subCatId);
    const children = getChildren(subCatId);

    if (children.length > 0) {
      // C'est une catégorie de niveau 1 avec des enfants — on la sélectionne comme catégorie
      onSelectCategory(subCatId);
      onSelectSubCategory(null);
      setExpandedRayons((prev) => new Set(prev).add(parentRayonId));
      toggleCategory(subCatId);
    } else if (subCat?.parent_id && getChildren(subCat.parent_id).length > 0) {
      // C'est potentiellement un niveau 2 (ou un niveau 1 sans enfants)
      // On vérifie si le parent est un rayon ou une catégorie
      const parent = categories.find((c) => c.id === subCat.parent_id);
      if (parent && !parent.parent_id) {
        // Parent est un rayon → c'est un enfant de niveau 1 sans sous-catégories
        onSelectCategory(parentRayonId);
        onSelectSubCategory(subCatId);
      } else {
        // Parent est une catégorie de niveau 1 → c'est un niveau 2
        onSelectCategory(parentRayonId);
        onSelectSubCategory(subCatId);
      }
    } else {
      onSelectCategory(parentRayonId);
      onSelectSubCategory(subCatId);
    }
  };

  const handleSelectAll = () => {
    onSelectCategory(null);
    onSelectSubCategory(null);
  };

  const isAllSelected = !selectedCategoryId && !selectedSubCategoryId;

  return (
    <nav className="shop-sans w-[220px] flex-shrink-0 bg-[#faf7f0] border-r border-black/[.08] overflow-y-auto">
      <div className="px-5 py-6">
        {/* Notre production — au-dessus des rayons, parce que c'est un filtre qui
            les traverse tous, pas un rayon de plus. Masqué s'il n'y a rien à
            filtrer aujourd'hui : une case qui ne donne aucun résultat déçoit. */}
        {ownProductionCount > 0 && (
          <label
            className={`flex items-center gap-2 px-3 py-[9px] mb-3 rounded-lg cursor-pointer transition-colors ${
              ownProductionOnly ? 'bg-[#4a5c3a] text-white' : 'text-[#4a4530] hover:bg-[#f0e9d8]'
            }`}
          >
            <input
              type="checkbox"
              checked={ownProductionOnly}
              onChange={(e) => onToggleOwnProduction(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#4a5c3a] focus:ring-[#4a5c3a]"
            />
            <img src="/logo-folies-badge.png" alt="" className="w-5 h-5 object-contain" />
            <span className="text-[13.5px] font-bold">Notre production</span>
            <span className={`ml-auto text-xs font-semibold ${ownProductionOnly ? 'text-white/80' : 'text-[#6b6550]'}`}>
              {ownProductionCount}
            </span>
          </label>
        )}

        {/* Anti-gaspi : DLC courte à -50 % — même logique que « Notre
            production » : filtre transversal, masqué quand il est vide. */}
        {promoCount > 0 && (
          <label
            className={`flex items-center gap-2 px-3 py-[9px] mb-3 rounded-lg cursor-pointer transition-colors ${
              promoOnly ? 'bg-[#c02626] text-white' : 'text-[#4a4530] hover:bg-[#f0e9d8]'
            }`}
          >
            <input
              type="checkbox"
              checked={promoOnly}
              onChange={(e) => onTogglePromo(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#c02626] focus:ring-[#c02626]"
            />
            <span aria-hidden>🏷️</span>
            <span className="text-[13.5px] font-bold">Anti-gaspi</span>
            <span className={`ml-auto text-xs font-semibold ${promoOnly ? 'text-white/80' : 'text-[#6b6550]'}`}>
              {promoCount}
            </span>
          </label>
        )}

        {/* Nouveautés de la semaine — troisième filtre transversal, même
            mécanique que les deux précédents. Le serveur ne marque un produit
            que tant que l'annonce est vraie : ce compteur tombe seul, et la
            case disparaît avec lui. Rien à éteindre à la main. */}
        {noveltyCount > 0 && (
          <label
            className={`flex items-center gap-2 px-3 py-[9px] mb-3 rounded-lg cursor-pointer transition-colors ${
              noveltyOnly ? 'bg-[#2d6a4f] text-white' : 'text-[#4a4530] hover:bg-[#f0e9d8]'
            }`}
          >
            <input
              type="checkbox"
              checked={noveltyOnly}
              onChange={(e) => onToggleNovelty(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#2d6a4f] focus:ring-[#2d6a4f]"
            />
            <span aria-hidden>✨</span>
            <span className="text-[13.5px] font-bold">Nouveautés</span>
            <span className={`ml-auto text-xs font-semibold ${noveltyOnly ? 'text-white/80' : 'text-[#6b6550]'}`}>
              {noveltyCount}
            </span>
          </label>
        )}

        {/* Mes favoris — en TETE des filtres transversaux : c'est le premier
            geste d'une cliente qui refait ses courses habituelles. Rendu
            seulement quand `onToggleFavoris` est fourni, c'est-a-dire pour un
            client connecte : sans compte il n'y a nulle part ou ranger une
            liste. Le compteur reste affiche meme a 0 pour que la case ne
            disparaisse pas sous les doigts en retirant le dernier favori. */}
        {onToggleFavoris && (
          <label
            className={`flex items-center gap-2 px-3 py-[9px] mb-3 rounded-lg cursor-pointer transition-colors ${
              favorisOnly ? 'bg-[#b45f2e] text-white' : 'text-[#4a4530] hover:bg-[#f0e9d8]'
            }`}
          >
            <input
              type="checkbox"
              checked={!!favorisOnly}
              onChange={(e) => onToggleFavoris(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#b45f2e] focus:ring-[#b45f2e]"
            />
            <span aria-hidden>❤️</span>
            <span className="text-[13.5px] font-bold">Mes favoris</span>
            <span className={`ml-auto text-xs font-semibold ${favorisOnly ? 'text-white/80' : 'text-[#6b6550]'}`}>
              {favorisCount ?? 0}
            </span>
          </label>
        )}

        {/* Tous les produits — pastille terracotta */}
        <button
          onClick={handleSelectAll}
          className={`w-full text-left px-3 py-[9px] text-[13.5px] font-bold rounded-lg transition-colors flex items-center justify-between mb-3.5 ${
            isAllSelected
              ? 'bg-[#b45f2e] text-white'
              : 'text-[#4a4530] hover:bg-[#f0e9d8]'
          }`}
        >
          <span>Tous les produits</span>
          <span className={`text-xs font-semibold ${isAllSelected ? 'text-white/80' : 'text-[#6b6550]'}`}>
            {totalProducts}
          </span>
        </button>

        {/* Rayons */}
        <div className="flex flex-col gap-0.5">
        {mainCategories.map((rayon) => {
          const children = getChildren(rayon.id);
          const isExpanded = expandedRayons.has(rayon.id);
          const isSelected = selectedCategoryId === rayon.id && !selectedSubCategoryId;
          const isActive = selectedCategoryId === rayon.id;
          const count = productCounts[rayon.id] || 0;

          return (
            <div key={rayon.id}>
              {/* Rayon */}
              <div className={`flex items-center rounded-md ${isSelected ? 'bg-[#f0e9d8]' : ''}`}>
                {children.length > 0 && (
                  <button
                    onClick={() => toggleRayon(rayon.id)}
                    className="pl-1.5 pr-0 py-2 text-[#6b6550] hover:text-[#4a4530] flex-shrink-0"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => handleSelectRayon(rayon.id)}
                  className={`flex-1 text-left py-2 pr-3 text-[13px] transition-colors flex items-center justify-between ${
                    children.length === 0 ? 'pl-3' : 'pl-1'
                  } ${
                    isSelected
                      ? 'font-bold text-[#b45f2e]'
                      : isActive
                        ? 'font-bold text-[#b45f2e]'
                        : 'text-[#4a4530] hover:bg-[#f0e9d8] rounded-md'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {rayon.icon && <span className="text-sm flex-shrink-0">{rayon.icon}</span>}
                    <span className="truncate">{rayon.name}</span>
                  </span>
                  <span className={`text-xs flex-shrink-0 ml-1 ${isActive ? 'text-[#b45f2e]/70 font-semibold' : 'text-[#6b6550]'}`}>
                    {count}
                  </span>
                </button>
              </div>

              {/* Sous-catégories (niveau 1) */}
              {isExpanded && children.length > 0 && (
                <div className="ml-3.5 border-l border-black/[.08]">
                  {children.map((child) => {
                    const grandChildren = getChildren(child.id);
                    const isCatExpanded = expandedCategories.has(child.id);
                    const isChildSelected = selectedSubCategoryId === child.id ||
                      (selectedCategoryId === child.id && !selectedSubCategoryId);
                    const childCount = categories
                      .filter((c) => c.parent_id === child.id)
                      .reduce((sum, c) => sum + (productCounts[c.id] || 0), 0) || productCounts[child.id] || 0;

                    return (
                      <div key={child.id}>
                        <div className={`flex items-center rounded-md ${isChildSelected ? 'bg-[#f0e9d8]' : ''}`}>
                          {grandChildren.length > 0 && (
                            <button
                              onClick={() => toggleCategory(child.id)}
                              className="pl-1.5 pr-0 py-1.5 text-[#6b6550] hover:text-[#4a4530] flex-shrink-0"
                            >
                              {isCatExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleSelectSubCategory(child.id, rayon.id)}
                            className={`flex-1 text-left py-1.5 pr-3 text-[13px] transition-colors ${
                              grandChildren.length === 0 ? 'pl-3' : 'pl-1'
                            } ${
                              isChildSelected
                                ? 'font-bold text-[#b45f2e]'
                                : 'text-[#4a4530] hover:bg-[#f0e9d8] rounded-md'
                            }`}
                          >
                            <span className="flex items-center justify-between">
                              <span className="truncate">{child.name}</span>
                              {childCount > 0 && (
                                <span className={`text-xs flex-shrink-0 ml-1 ${isChildSelected ? 'text-[#b45f2e]/70 font-semibold' : 'text-[#6b6550]'}`}>
                                  {childCount}
                                </span>
                              )}
                            </span>
                          </button>
                        </div>

                        {/* Sous-sous-catégories (niveau 2) */}
                        {isCatExpanded && grandChildren.length > 0 && (
                          <div className="ml-3.5 border-l border-black/[.08]">
                            {grandChildren.map((grandChild) => {
                              const isGrandChildSelected = selectedSubCategoryId === grandChild.id;
                              const gcCount = productCounts[grandChild.id] || 0;

                              return (
                                <button
                                  key={grandChild.id}
                                  onClick={() => {
                                    onSelectCategory(rayon.id);
                                    onSelectSubCategory(grandChild.id);
                                  }}
                                  className={`w-full text-left pl-3 pr-3 py-1.5 text-xs transition-colors rounded-md ${
                                    isGrandChildSelected
                                      ? 'font-bold text-[#b45f2e] bg-[#f0e9d8]'
                                      : 'text-[#6b6550] hover:bg-[#f0e9d8] hover:text-[#4a4530]'
                                  }`}
                                >
                                  <span className="flex items-center justify-between">
                                    <span className="truncate">{grandChild.name}</span>
                                    {gcCount > 0 && (
                                      <span className={`text-xs flex-shrink-0 ml-1 ${isGrandChildSelected ? 'text-[#b45f2e]/70' : 'text-[#6b6550]'}`}>
                                        {gcCount}
                                      </span>
                                    )}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </nav>
  );
}
